import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { Radar, RefreshCw } from "lucide-react";
import { getScanner, refreshScanner, getWatchlist, addWatch } from "../lib/api";
import { toast } from "sonner";
import { Card, Loader } from "./Primitives";
import ScanCard from "./ScanCard";

const ASSETS = [["equity", "Equity"], ["crypto", "Crypto"], ["debt", "Debt"]];
const MARKETS = [["india", "India"], ["global", "Foreign"]];
const SEGMENTS = [["largecap", "Large-cap"], ["midcap", "Mid-cap"], ["smallcap", "Small-cap"]];
const ACTIONS = [["all", "All"], ["buy", "Buy only"], ["gems", "Hidden gems"], ["goals", "Fits my goals"]];

const Pills = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map(([k, l]) => (
      <button key={k} data-testid={`${prefix}-${k}`} onClick={() => onChange(k)}
        className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-colors ${value === k ? "bg-[#00e5ff] text-[#06121a]" : "border border-[#2d3c59] text-[#94a3b8] hover:text-[#f8fafc]"}`}>{l}</button>
    ))}
  </div>
);

function pickList(d, asset, market, segment) {
  if (asset === "equity") return d.equity?.[market]?.[segment] || [];
  if (asset === "debt") return d.debt?.[market] || [];
  return d.crypto || [];
}

function applyFilter(list, f) {
  if (f === "buy") return list.filter((x) => ["Strong Buy", "Buy"].includes(x.action));
  if (f === "gems") return list.filter((x) => x.hidden_gem);
  if (f === "goals") return list.filter((x) => x.fits_goal && ["Strong Buy", "Buy"].includes(x.action));
  return list;
}

export default function MarketScan() {
  const qc = useQueryClient();
  const [asset, setAsset] = useState("equity");
  const [market, setMarket] = useState("india");
  const [segment, setSegment] = useState("largecap");
  const [filter, setFilter] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["scanner"], queryFn: getScanner,
    refetchInterval: (q) => (q.state.data?.building ? 8000 : false),
  });
  const refresh = async () => { await refreshScanner(); qc.invalidateQueries({ queryKey: ["scanner"] }); };
  const { data: wl } = useQuery({ queryKey: ["watchlist"], queryFn: getWatchlist });
  const watchedSet = new Set((wl?.items || []).map((w) => w.symbol));
  const watch = async (x) => {
    try {
      await addWatch(x);
      toast.success(`${x.name} added — you'll be alerted at buy, target or stop`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    qc.invalidateQueries({ queryKey: ["watchlist"] });
  };
  const [limit, setLimit] = useState(12);
  useEffect(() => setLimit(12), [asset, market, segment, filter]);
  const ready = data?.status === "ready";
  const full = ready ? applyFilter(pickList(data, asset, market, segment), filter) : [];
  const list = full.slice(0, limit);

  return (
    <div className="space-y-5" data-testid="market-scan">
      <Card className="p-5" testid="scan-controls">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Radar className="w-5 h-5 text-[#00e5ff]" /><div>
            <div className="text-sm font-semibold text-[#f8fafc]">Daily Market Scan · Berkshire-style</div>
            <div className="text-xs text-[#64748b]" data-testid="scan-meta">
              {ready ? `${data.stats.equities_scanned} equities · ${data.stats.crypto_scanned} crypto · ${data.stats.debt_scanned} debt · live Yahoo Finance · scanned ${new Date(data.generated_at).toLocaleString()}` : "Scanning markets…"}
            </div>
          </div></div>
          <button data-testid="scan-refresh-btn" onClick={refresh} disabled={data?.building}
            className="flex items-center gap-2 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium px-4 py-2.5 hover:bg-[#33eaff] disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${data?.building ? "animate-spin" : ""}`} /> {data?.building ? "Scanning…" : "Rescan now"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <Pills items={ASSETS} value={asset} onChange={setAsset} prefix="scan-asset" />
          {asset !== "crypto" && <Pills items={MARKETS} value={market} onChange={setMarket} prefix="scan-market" />}
          {asset === "equity" && <Pills items={SEGMENTS} value={segment} onChange={setSegment} prefix="scan-segment" />}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Pills items={ACTIONS} value={filter} onChange={setFilter} prefix="scan-filter" />
          {ready && <span className="text-[11px] font-mono text-[#64748b] ml-auto" data-testid="scan-count">Showing {list.length} of {full.length}</span>}
        </div>
      </Card>

      {isLoading || !ready ? <Loader label="Scanning 200+ instruments on Yahoo Finance… (first scan takes ~30s)" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 min-w-0" data-testid="scan-results">
          <AnimatePresence>{list.map((x, i) => <ScanCard key={x.symbol} x={x} i={i} watched={watchedSet.has(x.symbol)} onWatch={watch} />)}</AnimatePresence>
          {list.length === 0 && <Card className="p-10 text-center md:col-span-2 xl:col-span-3 text-sm text-[#64748b]" testid="scan-empty">No picks match this filter today.</Card>}
        </div>
      )}
      {ready && full.length > list.length && (
        <button data-testid="scan-show-more" onClick={() => setLimit((l) => l + 12)}
          className="mx-auto block px-4 py-2 rounded-lg border border-[#2d3c59] text-xs font-mono text-[#94a3b8] hover:text-[#f8fafc] transition-colors">
          Show {Math.min(12, full.length - list.length)} more
        </button>
      )}
      <p className="text-center text-[11px] text-[#64748b]">Deterministic factor scans on live Yahoo Finance data and analyst consensus. Educational research, not investment advice.</p>
    </div>
  );
}
